import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, PlusCircle } from 'lucide-react';
import complianceApi, { ComplianceCheck } from '../../api/compliance';
import PageHeader from '../../components/PageHeader';

const ComplianceManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [checks, setChecks] = useState<ComplianceCheck[]>([]);
  const [filteredChecks, setFilteredChecks] = useState<ComplianceCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');

  useEffect(() => {
    loadChecks();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [checks, filterStatus, filterType]);

  const loadChecks = async () => {
    try {
      setLoading(true);
      const data = await complianceApi.getAllChecks();
      setChecks(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load compliance checks');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...checks];

    if (filterStatus !== 'all') {
      if (filterStatus === 'expiring') {
        filtered = filtered.filter((c) => c.needsAttention && !c.isExpired);
      } else if (filterStatus === 'expired') {
        filtered = filtered.filter((c) => c.isExpired);
      } else {
        filtered = filtered.filter((c) => c.status === filterStatus);
      }
    }

    if (filterType !== 'all') {
      filtered = filtered.filter((c) => c.checkType === filterType);
    }

    setFilteredChecks(filtered);
  };

  const getStatusColor = (check: ComplianceCheck) => {
    if (check.isExpired) return 'bg-red-100 text-red-800';
    if (check.needsAttention) return 'bg-yellow-100 text-yellow-800';
    if (check.status === 'COMPLETED') return 'bg-green-100 text-green-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getStatusLabel = (check: ComplianceCheck) => {
    if (check.isExpired) return 'EXPIRED';
    if (check.needsAttention) return 'EXPIRING SOON';
    return check.status;
  };

  const checkTypes = [...new Set(checks.map((c) => c.checkType))];

  const summaryStats = {
    total: checks.length,
    active: checks.filter((c) => c.status === 'ACTIVE' && !c.isExpired).length,
    expiring: checks.filter((c) => c.needsAttention && !c.isExpired).length,
    expired: checks.filter((c) => c.isExpired).length,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
        <PageHeader
          title="Compliance Management"
          subtitle="Track and manage property compliance checks"
          icon={<ClipboardCheck className="w-8 h-8" />}
          backPath="/owner/dashboard"
        >
          <button
            onClick={() => navigate('/owner/compliance/create')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-10 text-[13px] font-semibold text-white bg-forest-900 hover:bg-forest-800 transition-colors shadow-sm"
          >
            <PlusCircle size={16} />
            Add Compliance Check
          </button>
        </PageHeader>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Checks</div>
            <div className="text-2xl font-bold text-gray-900">{summaryStats.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Active</div>
            <div className="text-2xl font-bold text-green-600">{summaryStats.active}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Expiring Soon</div>
            <div className="text-2xl font-bold text-yellow-600">{summaryStats.expiring}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Expired</div>
            <div className="text-2xl font-bold text-red-600">{summaryStats.expired}</div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="ACTIVE">Active</option>
                <option value="expiring">Expiring Soon</option>
                <option value="expired">Expired</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">Check Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                {checkTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredChecks.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No compliance checks</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding a new compliance check.</p>
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Property
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Check Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expiry Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Days Until Expiry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredChecks.map((check) => (
                  <tr key={check.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{check.propertyAddress}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{check.checkType.replace(/_/g, ' ')}</div>
                      {check.certificateNumber && (
                        <div className="text-sm text-gray-500">Cert: {check.certificateNumber}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {check.expiryDate || 'Not set'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {check.daysUntilExpiry !== undefined && (
                        <div
                          className={`text-sm font-medium ${
                            check.isExpired
                              ? 'text-red-600'
                              : check.needsAttention
                              ? 'text-yellow-600'
                              : 'text-green-600'
                          }`}
                        >
                          {check.isExpired
                            ? `${Math.abs(check.daysUntilExpiry)} days overdue`
                            : `${check.daysUntilExpiry} days`}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                          check
                        )}`}
                      >
                        {getStatusLabel(check)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => navigate(`/owner/compliance/${check.id}`)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        View
                      </button>
                      {check.certificateUrl && (
                        <a
                          href={check.certificateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-900"
                        >
                          Certificate
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
};

export default ComplianceManagementPage;


